import {
  Container,
  Box,
  Typography,
  Grid,
  useTheme,
  styled,
  List,
  ListItemText,
  ListItemButton,
  Stack,
} from '@mui/material';
import React from 'react';

//Components
// import ButtonBack from 'src/components/common/ButtonBack';
import Footer from 'src/components/Footer';
import { LIST_MENU_SIDEBAR_LIMITATION, MenuLimitation } from './config';
import ButtonBack from 'src/components/common/ButtonBack';

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'linear-gradient(180deg, #7551FF 0%, #3311DB 100%)',
  boxShadow: '0px 0px 50px 0px rgba(112, 114, 176, 0.08)',
  height: 250,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(7),
  marginTop: 8,
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(2),
  '& .MuiTypography-root': {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
  },
  '&.Mui-selected': {
    borderRight: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: 'transparent',
    '& .MuiTypography-root': {
      color: theme.palette.primary.main,
    },
  },
}));

const TradeDataLimitations = () => {
  const theme = useTheme();
  const [tab, setTab] = React.useState(MenuLimitation.DataRestrictionsReason);

  const onClick = (id: MenuLimitation) => {
    setTab(id);
    const ele = document.getElementById(id);
    if (ele) {
      window.scrollTo(ele.offsetLeft, ele.offsetTop);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        background: theme.palette.background.paper1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ButtonBack sx={{ position: 'absolute', top: 40, left: 40 }} color="inheritWhite" />
      <StyledBox>
        <Typography variant="h2" color="primary.contrastText" align="center">
          Trade Data Limitations
        </Typography>
        <Typography variant="body1" color="primary.contrastText" align="center">
          Last updated on December 16, 2024
        </Typography>
      </StyledBox>
      <StyledContainer maxWidth="xl">
        <Grid container spacing={8}>
          <Grid item xs={12} md={3}>
            <List>
              {LIST_MENU_SIDEBAR_LIMITATION.map((menu, index) => {
                return (
                  <StyledListItemButton
                    key={`cookies-policy-${menu.id}-${index}`}
                    onClick={() => onClick(menu.id)}
                    selected={tab === menu.id}
                  >
                    <ListItemText primary={<Typography variant="subtitle2">{menu.label}</Typography>} />
                  </StyledListItemButton>
                );
              })}
            </List>
          </Grid>
          <Grid item xs={12} md={9}>
            <Stack spacing={2}>
              <Typography variant="body1" color="text.secondary">
                At ADAMftd, we are committed to providing our users with transparent, reliable, and comprehensive trade
                data. However, due to economic, political, and competitive factors, certain countries and states impose
                restrictions on specific data fields within their trade information. These restrictions are determined
                by customs authorities or third-party data providers, and we aim to inform our users clearly about these
                limitations.
              </Typography>
              <Typography variant="h5" color="primary.main" fontWeight={700} id={MenuLimitation.DataRestrictionsReason}>
                Why Are There Data Restrictions?
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Data restrictions occur for various reasons, including:
              </Typography>
              <ul style={{ marginLeft: '2rem', color: theme.palette.text.secondary }}>
                <li style={{ marginBottom: '1rem' }}>
                  <b>Economic Policies:</b> Governments may withhold data to protect local industries or prevent market
                  exploitation.
                </li>
                <li style={{ marginBottom: '1rem' }}>
                  <b>Political Sensitivities:</b> Some data is restricted due to national security or political
                  considerations.
                </li>
                <li style={{ marginBottom: '1rem' }}>
                  <b>Competitive Concerns:</b> Authorities may limit access to certain details to safeguard competitive
                  advantages in trade.
                </li>
              </ul>
              <Typography variant="body1" color="text.secondary">
                While we strive to deliver the most complete and accurate trade data, these limitations are beyond our
                control and depend on the regulations of individual countries and their customs authorities.
              </Typography>
              <Typography
                variant="h5"
                color="primary.main"
                fontWeight={700}
                id={MenuLimitation.DataRestrictionsExample}
              >
                Examples of Data Restrictions
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Here are some notable examples of data restrictions enforced by various governments:
              </Typography>
              <Box>
                <Typography variant="body1" color="text.secondary" fontWeight={700}>
                  1. United States
                </Typography>
                <ul style={{ marginLeft: '2rem', color: theme.palette.text.secondary }}>
                  <li>The U.S. government does not provide the Value in USD field in Bill of Lading (B/L) data.</li>
                  <li>The U.S. government does not provide company names of foreign buyers of U.S. goods.</li>
                </ul>
              </Box>
              <Box>
                <Typography variant="body1" color="text.secondary" fontWeight={700}>
                  2. Argentina
                </Typography>
                <ul style={{ marginLeft: '2rem', color: theme.palette.text.secondary }}>
                  <li>The Argentine government restricts access to the B/L Number field in their trade data.</li>
                  <li>Argentina does not provide information on foreign suppliers or buyers outside of Argentina.</li>
                </ul>
              </Box>
              <Box>
                <Typography variant="body1" color="text.secondary" fontWeight={700}>
                  3. Other Countries
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Similar restrictions may exist for specific fields, such as product descriptions, exporter/importer
                  names, or port of origin/destination data, depending on local regulations.
                </Typography>
              </Box>
              <Typography variant="h5" color="primary.main" fontWeight={700} id={MenuLimitation.CommitmentTransparency}>
                Our Commitment to Transparency
              </Typography>
              <Typography variant="body1" color="text.secondary">
                At ADAMftd, we make every effort to:
              </Typography>
              <ul style={{ marginLeft: '2rem', color: theme.palette.text.secondary }}>
                <li style={{ marginBottom: '1rem' }}>Clearly notify users of any known data restrictions.</li>
                <li style={{ marginBottom: '1rem' }}>
                  Update our platform regularly as restrictions evolve or change.
                </li>
                <li style={{ marginBottom: '1rem' }}>
                  Advocate for greater transparency in global trade data while respecting the laws and policies of each
                  country.
                </li>
              </ul>
              <Typography variant="body1" color="text.secondary">
                We appreciate your understanding as we navigate these restrictions to bring you the most valuable
                insights into global trade. If you have any further questions regarding specific data limitations,
                please feel free to contact our support team.
              </Typography>
            </Stack>
          </Grid>
        </Grid>
        <Footer />
      </StyledContainer>
    </Box>
  );
};

export default TradeDataLimitations;
